import React, { useRef, useEffect, useState, useMemo } from 'react';
import { usePrevious } from '../../../../utils/custom-hooks';
import { connect } from 'react-redux';
import { selectCurrentDisplayedIncident } from '../../../../state/modules/incidents/selectors';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import GoogleStreetviewAPIWrapper from '../../../../utils/GoogleStreetviewAPIWrapper';
import GoogleMapReact from 'google-map-react';

// converts API coordinates to Google Map format
const convertCoords = coords => coords && coords.latitude && { lat: coords.latitude, lng: coords.longitude };

const IncidentStreetViewWidget = ({ displayedIncident }) => {
  const incidentCoords = useMemo(() => convertCoords(displayedIncident?.address), [displayedIncident]);
  const [panoInfo, setPanoInfo] = useState({});
  const mapRef = useRef(null);
  const panoramaRef = useRef(null);
  const previousDisplayedIncident = usePrevious(displayedIncident);

  // function to retrieve the pano ID and heading from the Google API
  const getPanoInfo = async () => {
    if (!incidentCoords) return;

    // Returns a special reference to a Streetview panorama ID, based on the
    // location and the angle between the street and the address
    const result = await GoogleStreetviewAPIWrapper.getInstance().fetchPanorama(incidentCoords);

    // If there is no result, clear the Street View
    if (!result) {
      setPanoInfo({});
      return;
    }

    setPanoInfo({ heading: result?.heading, pano: result?.pano });
  };

  // function to update the view according to the pano ID and heading as set in
  // the state
  const updatePanoramaView = () => {
    if (!mapRef.current || !panoInfo) return;

    const panorama = panoramaRef.current || mapRef.current.getStreetView();
    panoramaRef.current = panorama;
    panorama.setPano(panoInfo.pano);
    panorama.setPov({ heading: panoInfo.heading, pitch: 0 });
    panorama.setVisible(true);
    panorama.setOptions({ disableDefaultUI: true });
    panorama.setEnableCloseButton(false);
  };

  // Function to clean up the panorama when the component unmounts.
  // This is important to avoid memory leaks and ensure that WebGL
  // textures are properly released.
  const cleanupPanorama = () => {
    if (panoramaRef.current) {
      // 1. Hide panorama to force browser to drop WebGL textures
      panoramaRef.current.setVisible(false);

      // 2. Clear all internal listeners attached to this StreetView instance
      if (globalThis.google && globalThis.google.maps) {
        globalThis.google.maps.event.clearInstanceListeners(panoramaRef.current);
      }
    }
  };

  // get pano info every time the incident coords change. Note: this is an async
  // function, so we have to call it this way
  useEffect(() => {
    getPanoInfo();
  }, [incidentCoords]);
  // set the panorama every time the panoInfo changes
  useEffect(updatePanoramaView, [panoInfo]);

  useEffect(() => {
    if (previousDisplayedIncident && !displayedIncident) {
      cleanupPanorama();
    }
  }, [displayedIncident]);

  useEffect(() => {
    return cleanupPanorama;
  }, []);

  const onGoogleApiLoaded = ({ map }) => {
    mapRef.current = map;
    updatePanoramaView();
  };

  if (!displayedIncident) return <WaitingForIncidentMessage />;

  if (!panoInfo.pano) return <h3>{I18n.t('incidents.location_unknown')}</h3>;

  return (
    <GoogleMapReact
      bootstrapURLKeys={{ key: globalThis.mapsApiKey }}
      defaultZoom={14}
      defaultCenter={incidentCoords || {}}
      onGoogleApiLoaded={onGoogleApiLoaded}
      yesIWantToUseGoogleMapApiInternals
    />
  );
};

const mapStateToProps = state => ({
  displayedIncident: selectCurrentDisplayedIncident(state)
});

export default connect(mapStateToProps)(IncidentStreetViewWidget);
